import { MouseEventHandler, PropsWithChildren } from 'react';

import styled from '@emotion/styled';

import { ArrowLinkIcon, LockIcon } from '@web/ui/icons';
import { ThemeColorKey } from '@web/ui/theme/type';
import { getStateLayerColor } from '@web/ui/theme/utils';

export const HubClasses = {
  topRowContainer: 'topRowContainer',
};

const Icons = {
  link: <ArrowLinkIcon size="0625" />,
  lock: <LockIcon size="0625" />,
};

const HubTileContainer = styled.div<{
  background?: ThemeColorKey;
  stateLayer?: ThemeColorKey;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${({ background, theme }) =>
    background && theme.color[background]};
  border-radius: ${({ theme }) => theme.shape.corner['2000']};
  cursor: pointer;
  transition: all 100ms;
  overflow: clip;
  height: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing('2000')};

  &:hover {
    background-image: ${({ theme, stateLayer }) =>
      stateLayer
        ? getStateLayerColor(stateLayer, 'hover')({ theme })
        : 'transparent'};
  }

  :active {
    border-radius: ${({ theme }) => theme.shape.corner['2500']};
    background-image: ${({ theme, stateLayer }) =>
      stateLayer
        ? getStateLayerColor(stateLayer, 'active')({ theme })
        : 'transparent'};
  }
`;

const TopRowContainer = styled.div<{ textColor?: ThemeColorKey }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing('1000')};
  color: ${({ theme, textColor }) => textColor && theme.color[textColor]};
  z-index: 2;
`;

const Title = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};
`;

const BadgeAndIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const Badge = styled.div`
  width: fit-content;
`;

interface IHubTileProps {
  title?: string;
  icon?: 'link' | 'lock';
  badge?: React.ReactNode;
  textColor?: ThemeColorKey;
  background?: ThemeColorKey;
  stateLayer?: ThemeColorKey;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

export const HubTile = ({
  background,
  icon,
  badge,
  stateLayer,
  textColor,
  title,
  children,
  className,
  onClick,
}: PropsWithChildren<IHubTileProps>) => {
  return (
    <HubTileContainer
      onClick={onClick}
      background={background}
      stateLayer={stateLayer}
      className={className}
    >
      {(title || icon) && (
        <TopRowContainer
          textColor={textColor}
          className={HubClasses.topRowContainer}
        >
          {title && <Title>{title}</Title>}
          <BadgeAndIcon>
            {badge && <Badge>{badge}</Badge>}
            {icon && Icons[icon]}
          </BadgeAndIcon>
        </TopRowContainer>
      )}
      {children}
    </HubTileContainer>
  );
};
